import { ApolloClient, gql } from 'apollo-boost';
import {
  CommentsBySectionIdResponse,
  isCustomQuestion,
  parseCustomQuestionId,
} from 'donor/portal/pages/FeedbackDashboard/Feedback/api';
import { SelectedFeedback } from './utils';
import {
  FeedbackSectionId,
  UserType,
} from 'donor/portal/pages/FeedbackDashboard/Feedback/utils';
import { FeedbackStatus } from 'donor/portal/pages/FeedbackDashboard/api';
import { SuccessResponse } from '../ReportsBeta/NewReport/api';

const getCommentsByReportIdQuery = gql`
  query allNpoReportComments($reportId: Int!) {
    allNpoReportComments(filters: { reportId: $reportId }) {
      edges {
        node {
          commentId
          userName
          userEmail
          createdAt
          userId
          details
          comment
          userType
          questionId
          questionText
          sectionId
          reportId
          surveyQuestionId
        }
      }
    }
  }
`;

export const getCommentsByReportId = (
  client: ApolloClient<object>,
  reportId: number | string,
) => {
  return client.query<CommentsBySectionIdResponse>({
    query: getCommentsByReportIdQuery,
    variables: {
      reportId,
    },
    fetchPolicy: 'network-only',
  });
};

const markAsResolvedOrVerifiedQuery = gql`
  mutation answersFeedback(
    $reportId: Int!
    $sectionId: String!
    $questionId: Int
    $markAsResolved: Boolean
    $markAsVerified: Boolean
    $surveyQuestionId: Int
  ) {
    answersFeedback(
      reportId: $reportId
      sectionId: $sectionId
      questionId: $questionId
      markAsResolved: $markAsResolved
      markAsVerified: $markAsVerified
      surveyQuestionId: $surveyQuestionId
    ) {
      id
      success
    }
  }
`;

const getMarkingVariables = (
  reportId: number | string,
  userType: UserType,
  selectedSection: SelectedFeedback,
  feedbackStatus: FeedbackStatus,
  done = true,
  isCustomQuestionInit = false,
) => {
  let mark: 'markAsResolved' | 'markAsVerified' = 'markAsResolved';
  switch (feedbackStatus) {
    case 'Awaiting TI Verification':
    case 'Awaiting Funder Verification':
      mark = 'markAsVerified';
      break;

    default:
      break;
  }
  const questionId = selectedSection.question?.questionId;
  const isCustom = isCustomQuestionInit || isCustomQuestion(questionId);

  return {
    reportId,
    questionId: isCustom ? undefined : questionId,
    surveyQuestionId: isCustom
      ? parseCustomQuestionId(questionId) || questionId
      : undefined,
    sectionId: selectedSection.section.sectionId,
    [mark]: done,
  };
};

export const markAsResolvedOrVerified = (
  client: ApolloClient<object>,
  reportId: number | string,
  userType: UserType,
  selectedSection: SelectedFeedback,
  feedbackStatus: FeedbackStatus,
  done = true,
  isCustomQuestion = false,
) => {
  return client.mutate<SuccessResponse<'answersFeedback'>>({
    mutation: markAsResolvedOrVerifiedQuery,
    variables: getMarkingVariables(
      reportId,
      userType,
      selectedSection,
      feedbackStatus,
      done,
      isCustomQuestion,
    ),
    fetchPolicy: 'no-cache',
  });
};

const getAnswersFeedbackQuery = gql`
  query allAnswersFeedback($reportId: Int!) {
    allAnswersFeedback(filters: { reportId: $reportId }) {
      edges {
        node {
          id
          questionId
          sectionId
          reportId
          createdAt
          updatedAt
          resolved
          resolvedCount
          verified
          lastResolved
          surveyQuestionId
        }
      }
    }
  }
`;

export interface AnswersFeedbacks {
  allAnswersFeedback: {
    id: string;
    questionId?: number;
    surveyQuestionId?: number;
    sectionId: FeedbackSectionId;
    reportId: number;
    createdAt: string;
    updatedAt: string | null;
    resolved: string | null;
    resolvedCount: number;
    verified: string | null;
    lastResolved: string | null;
  }[];
}

export const getAnswersFeedback = (
  client: ApolloClient<object>,
  reportId: number | string,
) => {
  return client.query<AnswersFeedbacks>({
    query: getAnswersFeedbackQuery,
    variables: {
      reportId,
    },
    fetchPolicy: 'network-only',
  });
};
